import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from '../../firebase';
import SpinnerButton from '../../components/Button/SpinnerButton';
import { Container, Row, Col, Form, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useApiStore from '../../stores/apiStore';

function RegisterScreen() {
    const { t } = useTranslation();
    const [error, setError] = useState();
    const [success, setSuccess] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(true);
    const navigate = useNavigate();
    const { sendVerificationEmail } = useApiStore();

    const getPasswordErrorMessage = (fieldId) => {
        const field = document.getElementById(fieldId);
        if (!field) return t('register.passwordRequired');

        if (field.validity.valueMissing) {
            return t('register.passwordRequired');
        } else if (field.validity.tooShort) {
            return t('errors.passwordMinLength');
        }
        return t('register.passwordRequired');
    };

    const handleRegister = async (event) => {
        setSuccess(false)
        setError();
        event.preventDefault();
        event.stopPropagation();

        const form = event.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return
        }

        const email = form.elements.inputEmail.value;
        const password = form.elements.inputPassword.value;
        const confirmPassword = form.elements.inputConfirmPassword.value;

        if (password !== confirmPassword) {
            setPasswordsMatch(false);
            return;
        }

        setPasswordsMatch(true);
        setLoading(true);

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password)
            const user = userCredential.user;
            const errorCode = await sendVerificationEmail(user);
            if (errorCode) {
                setError(t(errorCode));
            } else {
                setSuccess(true);
            }
        } catch (err) {
            const errorCode = err.code;
            if (errorCode === 'auth/email-already-in-use') {
                setError(t('errors.emailInUse'));
            } else {
                setError(t('errors.incorrectCredentials'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-5" style={{ maxWidth: '400px' }}>
            <Row className="mb-5">
                <Col xs={12} className="text-center">
                    <h2 className="title">{t('register.title')}</h2>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Form noValidate validated={validated} onSubmit={handleRegister}>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="inputEmail">{t('register.email')}</Form.Label>
                            <Form.Control
                                required
                                id="inputEmail"
                                type="email"
                                placeholder={t('register.emailPlaceholder')}
                                disabled={isLoading}
                            />
                            <Form.Control.Feedback type="invalid">
                                {t('register.emailValid')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label htmlFor="inputPassword">{t('register.password')}</Form.Label>
                            <Form.Control
                                required
                                id="inputPassword"
                                type="password"
                                placeholder={t('register.passwordPlaceholder')}
                                disabled={isLoading}
                                minLength="6"
                            />
                            <Form.Control.Feedback type="invalid">
                                {getPasswordErrorMessage('inputPassword')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label htmlFor="inputConfirmPassword">{t('register.confirmPassword')}</Form.Label>
                            <Form.Control
                                required
                                id="inputConfirmPassword"
                                type="password"
                                placeholder={t('register.confirmPasswordPlaceholder')}
                                disabled={isLoading}
                                className={!passwordsMatch ? 'is-invalid' : ''}
                                minLength="6"
                            />
                            <Form.Control.Feedback type="invalid">
                                {!passwordsMatch ? t('errors.passwordsMismatch') : getPasswordErrorMessage('inputConfirmPassword')}
                            </Form.Control.Feedback>
                        </Form.Group>
                        {error && <div className="text-danger mb-3">{error}</div>}
                        {success && <div className="text-success mb-3">{t('register.sendEmailVerification')}</div>}
                        <Stack gap={3}>
                            <SpinnerButton
                                type="submit"
                                disabled={isLoading}
                                className="w-100 bg-pink"
                                label={t('register.signUp')}
                            />
                            <SpinnerButton
                                onClick={() => navigate('/login')}
                                className="w-100"
                                label={t('register.alreadyHaveAccount')}
                            />
                        </Stack>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
}

export default RegisterScreen;
